import { BleTransport } from '../ble/transport';
import { parseFactoryInfoLines } from '../ble/toolbox-protocol';
import {
  buildDiagnosticDownloadJson,
  buildDiagnosticReportPayload,
  type DiagnosticMetadata,
  type DiagnosticReportSummary,
  type DiagnosticTargetSweep,
  type DiagnosticTargetSweepStatus,
} from '../report/diagnostic-json';
import {
  type HardwareConfig,
  type HardwareRevisionConfig,
  isOperationalNumericConfigValue,
  normalizeConfigIdentifier,
  type TuningTargetConfig,
} from '../hardware-config/schema';
import {
  runSweep,
  type SweepPauseChangeDetails,
  type SweepPointProgressDetails,
  type SweepResult,
} from './sweep-runner';

const DIAGNOSTIC_FREQUENCY_MIN_HZ = 20000;
const DIAGNOSTIC_FREQUENCY_MAX_HZ = 60000;
const DIAGNOSTIC_TARGET_SWEEP_TIMEOUT_MS = 60000;
const DIAGNOSTIC_SWEEP_SEGMENT_START_STABILIZATION_MS = 500;
const DIAGNOSTIC_STARTUP_DISCARD_POINT_COUNT = 3;

export type DiagnosticConfigErrorReason = 'config_unavailable' | 'no_operational_targets';

export class DiagnosticConfigError extends Error {
  readonly reason: DiagnosticConfigErrorReason;

  constructor(reason: DiagnosticConfigErrorReason, message: string) {
    super(message);
    this.name = 'DiagnosticConfigError';
    this.reason = reason;
  }
}

export class DiagnosticUnsupportedHardwareError extends Error {
  readonly revision: string;

  constructor(revision: string) {
    super(`Diagnostics unsupported for hardware revision "${revision}".`);
    this.name = 'DiagnosticUnsupportedHardwareError';
    this.revision = revision;
  }
}

export type DiagnosticConfigInput = {
  hardwareConfig?: HardwareConfig | null;
  hardwareRevision?: string | null;
};

export type DiagnosticTargetSweepProgress = {
  targetId: string;
  status: DiagnosticTargetSweepStatus;
  totalTargets: number;
  completedTargets: number;
  failedTargets: number;
  skippedTargets: number;
};

export type DiagnosticTargetSweepPointProgress = {
  targetId: string;
  targetIndex: number;
  totalTargets: number;
  pointCount: number;
  expectedPointCount: number;
  progressPercent: number | null;
};

export type DiagnosticReport = {
  matchedRevisionId: string;
  factoryInfo: Record<string, string>;
  metadata: DiagnosticMetadata;
  targetSweeps: Record<string, DiagnosticTargetSweep>;
  summary: DiagnosticReportSummary;
  json: string;
  fileName: string;
};

export type DiagnosticSweepPauseChangeDetails = {
  targetId: string;
  pointCount: number;
  lastFrequencyHz: number | null;
};

export type DiagnosticOptions = {
  signal?: AbortSignal;
  onStatus?: (message: string) => void;
  onDebug?: (message: string, details?: Record<string, unknown>) => void;
  onTargetSweepProgress?: (progress: DiagnosticTargetSweepProgress) => void;
  onTargetSweepPointProgress?: (progress: DiagnosticTargetSweepPointProgress) => void;
  shouldPause?: () => boolean;
  onPauseChange?: (paused: boolean, details: DiagnosticSweepPauseChangeDetails) => void;
  obfuscationSecret?: string | null;
  obfuscationKeyId?: string;
};

type DiagnosticSweepSpec = {
  startHz: number;
  endHz: number;
  stepHz: number;
  dwellMs: number;
  timeoutMs: number;
};

type DiagnosticSweepPlan = {
  reportSpec: DiagnosticSweepSpec;
  runSpec: DiagnosticSweepSpec;
  startupDiscardPointCount: number;
};

type SweepSpecResolution =
  | { ok: true; plan: DiagnosticSweepPlan }
  | { ok: false; reason: string };

export async function runDiagnosticReport(
  transport: BleTransport,
  configInput: DiagnosticConfigInput,
  options: DiagnosticOptions = {}
): Promise<DiagnosticReport> {
  options.onDebug?.('Diagnostic report run started', {
    capabilities: transport.getCapabilities(),
    hasDeviceInfo: Boolean(transport.getDeviceInfo()),
    hasHardwareConfig: Boolean(configInput.hardwareConfig),
    reportedHardwareRevision: configInput.hardwareRevision ?? null,
  });

  const config = configInput.hardwareConfig;
  if (!config) {
    throw new DiagnosticConfigError('config_unavailable', 'Diagnostics configuration is unavailable.');
  }

  const revision = resolveDiagnosticRevision(config, configInput.hardwareRevision);
  if (revision.tuning_targets.length === 0) {
    throw new DiagnosticConfigError(
      'no_operational_targets',
      `Revision "${revision.revision_id}" has no tuning targets configured.`
    );
  }

  options.onStatus?.('Collecting knife details...');
  let factoryInfo: Record<string, string> = {};
  try {
    const response = await transport.sendTextCommandWithResponse('GetFactoryInfo', 3000);
    factoryInfo = parseFactoryInfoLines(response.split('\n'));
    transport.applyFactoryInfo(factoryInfo);
    options.onDebug?.('Factory info collected', {
      keys: Object.keys(factoryInfo),
    });
  } catch (error) {
    factoryInfo = {};
    options.onDebug?.('Factory info collection failed', {
      error: getErrorMessage(error),
    });
  }

  const targetSweeps: Record<string, DiagnosticTargetSweep> = {};
  const totalTargets = revision.tuning_targets.length;
  let completedTargets = 0;
  let failedTargets = 0;
  let skippedTargets = 0;
  let totalPointCount = 0;

  options.onStatus?.(`Running diagnostics for ${totalTargets} target${totalTargets === 1 ? '' : 's'}...`);

  for (let index = 0; index < totalTargets; index += 1) {
    const target = revision.tuning_targets[index];
    const targetLabel = `Target ${index + 1}/${totalTargets}`;
    const targetBase = buildTargetSweepBase(target);
    const resolvedSpec = resolveTargetSweepSpec(target);

    if (!resolvedSpec.ok) {
      skippedTargets += 1;
      const skippedSweep: DiagnosticTargetSweep = {
        ...targetBase,
        status: 'skipped',
        error: resolvedSpec.reason,
        sweep: null,
        points: [],
      };
      targetSweeps[target.target_id] = skippedSweep;
      options.onDebug?.('Skipping diagnostic target sweep', {
        targetId: target.target_id,
        reason: resolvedSpec.reason,
      });
      emitTargetSweepProgress(options, {
        targetId: target.target_id,
        status: 'skipped',
        totalTargets,
        completedTargets,
        failedTargets,
        skippedTargets,
      });
      continue;
    }

    const sweepPlan = resolvedSpec.plan;
    options.onStatus?.(`${targetLabel}: Preparing sweep...`);
    options.onDebug?.('Running diagnostic target sweep', {
      revisionId: revision.revision_id,
      targetId: target.target_id,
      spec: sweepPlan.reportSpec,
      runSpec: sweepPlan.runSpec,
      startupDiscardPointCount: sweepPlan.startupDiscardPointCount,
    });

    try {
      const rawSweep = await runSweep(transport, sweepPlan.runSpec, {
        signal: options.signal,
        onStatus: (message) => {
          options.onStatus?.(
            `${targetLabel}: ${formatDiagnosticSweepStatusMessage(message, sweepPlan.reportSpec)}`
          );
        },
        segmentStartStabilizationMs: DIAGNOSTIC_SWEEP_SEGMENT_START_STABILIZATION_MS,
        onPointProgress: (details) => {
          const adjustedDetails = adjustPointProgressForDiscardedStartupPoints(
            details,
            sweepPlan.startupDiscardPointCount
          );
          options.onTargetSweepPointProgress?.({
            targetId: target.target_id,
            targetIndex: index + 1,
            totalTargets,
            pointCount: adjustedDetails.pointCount,
            expectedPointCount: adjustedDetails.expectedPointCount,
            progressPercent: adjustedDetails.progressPercent,
          });
        },
        shouldPause: options.shouldPause,
        onPauseChange: (paused, details) => {
          const adjustedDetails = adjustPauseDetailsForDiscardedStartupPoints(
            details,
            sweepPlan.startupDiscardPointCount
          );
          options.onPauseChange?.(paused, {
            targetId: target.target_id,
            pointCount: adjustedDetails.pointCount,
            lastFrequencyHz: adjustedDetails.lastFrequencyHz,
          });
        },
        onDebug: options.onDebug,
      });
      const sweep = discardInitialSweepPoints(rawSweep, sweepPlan.startupDiscardPointCount);
      const discardedPointCount = rawSweep.points.length - sweep.points.length;
      if (discardedPointCount > 0) {
        options.onDebug?.('Discarded initial diagnostic sweep points during startup filtering', {
          targetId: target.target_id,
          configuredStartHz: sweepPlan.reportSpec.startHz,
          runStartHz: sweepPlan.runSpec.startHz,
          configuredEndHz: sweepPlan.reportSpec.endHz,
          configuredSkipPointCount: sweepPlan.startupDiscardPointCount,
          discardedPointCount,
          retainedPointCount: sweep.points.length,
        });
      }

      completedTargets += 1;
      totalPointCount += sweep.points.length;
      targetSweeps[target.target_id] = {
        ...targetBase,
        status: 'completed',
        error: null,
        sweep: {
          spec: {
            startHz: sweepPlan.reportSpec.startHz,
            endHz: sweepPlan.reportSpec.endHz,
            stepHz: sweepPlan.reportSpec.stepHz,
            dwellMs: sweepPlan.reportSpec.dwellMs,
          },
          completionReason: sweep.completionReason,
          startedAtMs: sweep.startedAtMs,
          endedAtMs: sweep.endedAtMs,
          pointCount: sweep.points.length,
        },
        points: sweep.points,
      };

      emitTargetSweepProgress(options, {
        targetId: target.target_id,
        status: 'completed',
        totalTargets,
        completedTargets,
        failedTargets,
        skippedTargets,
      });
    } catch (error) {
      if (isCancellationError(error, options.signal)) {
        throw error;
      }

      failedTargets += 1;
      const errorMessage = getErrorMessage(error);
      targetSweeps[target.target_id] = {
        ...targetBase,
        status: 'failed',
        error: errorMessage,
        sweep: null,
        points: [],
      };
      options.onStatus?.(`${targetLabel} failed. Continuing with remaining targets...`);
      options.onDebug?.('Diagnostic target sweep failed', {
        targetId: target.target_id,
        error: errorMessage,
      });
      emitTargetSweepProgress(options, {
        targetId: target.target_id,
        status: 'failed',
        totalTargets,
        completedTargets,
        failedTargets,
        skippedTargets,
      });
    }
  }

  if (transport.getDeviceInfo()?.voltageBattery == null) {
    options.onDebug?.('Battery voltage missing from live status, querying getStatus fallback.');
    const refreshed = await transport.refreshStatusFromCommand(2500);
    options.onDebug?.('getStatus fallback complete', {
      refreshed,
      voltageBattery: transport.getDeviceInfo()?.voltageBattery ?? null,
    });
  }

  const metadata = buildDiagnosticMetadata(transport, factoryInfo);
  const fileName = buildDiagnosticFileName(metadata);
  const summary: DiagnosticReportSummary = {
    totalTargets,
    completedTargets,
    failedTargets,
    skippedTargets,
    totalPointCount,
  };

  const reportPayload = buildDiagnosticReportPayload({
    metadata,
    targetSweeps,
    summary,
    fileName,
  });
  const download = await buildDiagnosticDownloadJson({
    report: reportPayload,
    obfuscationSecret: options.obfuscationSecret,
    obfuscationKeyId: options.obfuscationKeyId,
  });
  options.onDebug?.('Diagnostic report built', {
    ...summary,
    metadataKeys: Object.keys(metadata).length,
    fileName,
    jsonLength: download.json.length,
    downloadMode: download.mode,
    obfuscationKeyId: download.keyId,
  });

  return {
    matchedRevisionId: revision.revision_id,
    factoryInfo,
    metadata,
    targetSweeps,
    summary,
    json: download.json,
    fileName,
  };
}

function emitTargetSweepProgress(
  options: DiagnosticOptions,
  progress: DiagnosticTargetSweepProgress
): void {
  options.onTargetSweepProgress?.(progress);
}

function resolveDiagnosticRevision(
  config: HardwareConfig,
  reportedRevision: string | null | undefined
): HardwareRevisionConfig {
  const normalizedReported = normalizeConfigIdentifier(normalizeReportedRevision(reportedRevision));
  const matchedRevision = config.hardware_revisions.find(
    (candidate) => normalizeConfigIdentifier(candidate.revision_id) === normalizedReported
  );
  if (!matchedRevision) {
    throw new DiagnosticUnsupportedHardwareError(normalizeReportedRevision(reportedRevision));
  }
  return matchedRevision;
}

function resolveTargetSweepSpec(target: TuningTargetConfig): SweepSpecResolution {
  if (!isOperationalNumericConfigValue(target.center_frequency_hz)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational center_frequency_hz.`,
    };
  }
  if (!isOperationalNumericConfigValue(target.search_bandwidth_hz)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational search_bandwidth_hz.`,
    };
  }
  if (!isOperationalNumericConfigValue(target.initial_tuning.scan_start_freq_hz)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational initial_tuning.scan_start_freq_hz.`,
    };
  }
  if (!isOperationalNumericConfigValue(target.initial_tuning.scan_end_freq_hz)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational initial_tuning.scan_end_freq_hz.`,
    };
  }
  if (!isOperationalNumericConfigValue(target.initial_tuning.coarse_step_hz)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational initial_tuning.coarse_step_hz.`,
    };
  }
  if (!isOperationalNumericConfigValue(target.initial_tuning.dwell_time_ms)) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has non-operational initial_tuning.dwell_time_ms.`,
    };
  }

  const startHz = clampToDiagnosticRange(target.initial_tuning.scan_start_freq_hz);
  const endHz = clampToDiagnosticRange(target.initial_tuning.scan_end_freq_hz);
  const bandwidthHz = Math.round(target.search_bandwidth_hz);
  const stepHz = Math.round(target.initial_tuning.coarse_step_hz);
  const dwellMs = Math.round(target.initial_tuning.dwell_time_ms);

  if (bandwidthHz < 0) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has invalid negative search_bandwidth_hz.`,
    };
  }
  if (stepHz <= 0) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has invalid initial_tuning.coarse_step_hz.`,
    };
  }
  if (dwellMs <= 0) {
    return {
      ok: false,
      reason: `Target "${target.target_id}" has invalid initial_tuning.dwell_time_ms.`,
    };
  }

  return {
    ok: true,
    plan: buildDiagnosticSweepPlan({
      startHz,
      endHz,
      stepHz,
      dwellMs,
      timeoutMs: DIAGNOSTIC_TARGET_SWEEP_TIMEOUT_MS,
    }),
  };
}

function buildTargetSweepBase(target: TuningTargetConfig): Omit<DiagnosticTargetSweep, 'status' | 'error' | 'sweep' | 'points'> {
  return {
    targetId: target.target_id,
    description: target.description,
    priority: target.priority,
    centerFrequencyHz: toRoundedOrDefault(target.center_frequency_hz),
    searchBandwidthHz: toRoundedOrDefault(target.search_bandwidth_hz),
  };
}

function toRoundedOrDefault(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value);
}

function clampToDiagnosticRange(value: number): number {
  return Math.max(
    DIAGNOSTIC_FREQUENCY_MIN_HZ,
    Math.min(DIAGNOSTIC_FREQUENCY_MAX_HZ, Math.round(value))
  );
}

function formatDiagnosticSweepStatusMessage(message: string, reportSpec: DiagnosticSweepSpec): string {
  const match = message.match(/^Sweeping\s+([\d,]+)\s+to\s+([\d,]+)\s+Hz\.\.\.$/i);
  if (!match) {
    return message;
  }

  const parsedStartHz = Number(match[1].replaceAll(',', ''));
  if (!Number.isFinite(parsedStartHz)) {
    return message;
  }

  const displayedStartHz = clampToConfiguredSweepRange(parsedStartHz, reportSpec);
  const displayedEndHz = Math.round(reportSpec.endHz).toLocaleString('en-US');
  return `Sweeping ${displayedStartHz.toLocaleString('en-US')} to ${displayedEndHz} Hz...`;
}

function clampToConfiguredSweepRange(value: number, reportSpec: DiagnosticSweepSpec): number {
  const minHz = Math.min(reportSpec.startHz, reportSpec.endHz);
  const maxHz = Math.max(reportSpec.startHz, reportSpec.endHz);
  return Math.max(minHz, Math.min(maxHz, Math.round(value)));
}

function buildDiagnosticSweepPlan(spec: DiagnosticSweepSpec): DiagnosticSweepPlan {
  const startupPreroll = resolveSweepStartupPreroll({
    configuredStartHz: spec.startHz,
    configuredEndHz: spec.endHz,
    stepHz: spec.stepHz,
    desiredPointCount: DIAGNOSTIC_STARTUP_DISCARD_POINT_COUNT,
  });

  return {
    reportSpec: spec,
    runSpec: {
      ...spec,
      startHz: startupPreroll.sweepStartHz,
    },
    startupDiscardPointCount: startupPreroll.actualPointCount,
  };
}

function discardInitialSweepPoints(sweep: SweepResult, skipCount: number): SweepResult {
  if (skipCount <= 0 || sweep.points.length <= 1) {
    return sweep;
  }

  const clampedSkipCount = Math.min(skipCount, sweep.points.length - 1);
  if (clampedSkipCount <= 0) {
    return sweep;
  }

  return {
    ...sweep,
    points: sweep.points.slice(clampedSkipCount),
  };
}

function resolveSweepStartupPreroll(input: {
  configuredStartHz: number;
  configuredEndHz: number;
  stepHz: number;
  desiredPointCount: number;
}): { sweepStartHz: number; actualPointCount: number } {
  const direction: 1 | -1 = input.configuredEndHz >= input.configuredStartHz ? 1 : -1;
  const availableHz =
    direction > 0
      ? input.configuredStartHz - DIAGNOSTIC_FREQUENCY_MIN_HZ
      : DIAGNOSTIC_FREQUENCY_MAX_HZ - input.configuredStartHz;
  const availablePointCount = Math.max(0, Math.floor(availableHz / input.stepHz));
  const actualPointCount = Math.min(input.desiredPointCount, availablePointCount);

  return {
    sweepStartHz: input.configuredStartHz - direction * actualPointCount * input.stepHz,
    actualPointCount,
  };
}

function adjustPointProgressForDiscardedStartupPoints(
  details: SweepPointProgressDetails,
  skipCount: number
): SweepPointProgressDetails {
  if (skipCount <= 0) {
    return details;
  }

  const pointCount = Math.max(0, details.pointCount - skipCount);
  const expectedPointCount = Math.max(0, details.expectedPointCount - skipCount);
  const progressPercent =
    expectedPointCount > 0
      ? Math.min(100, Math.round((pointCount / expectedPointCount) * 100))
      : null;

  return {
    pointCount,
    expectedPointCount,
    progressPercent,
  };
}

function adjustPauseDetailsForDiscardedStartupPoints(
  details: SweepPauseChangeDetails,
  skipCount: number
): SweepPauseChangeDetails {
  if (skipCount <= 0) {
    return details;
  }

  const pointCount = Math.max(0, details.pointCount - skipCount);
  return {
    pointCount,
    lastFrequencyHz: pointCount > 0 ? details.lastFrequencyHz : null,
  };
}

function normalizeReportedRevision(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : 'unknown';
}

function isCancellationError(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) {
    return true;
  }
  return error instanceof Error && error.message === 'Sweep canceled.';
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function buildDiagnosticMetadata(
  transport: BleTransport,
  factoryInfo: Record<string, string>
): DiagnosticMetadata {
  const now = new Date();
  const info = transport.getDeviceInfo();

  return {
    generated_at_iso: now.toISOString(),
    generated_at_local: now.toLocaleString(),
    browser_user_agent: navigator.userAgent,
    device_name: info?.name ?? 'unknown',
    device_id: info?.id ?? 'unknown',
    factory_serial: factoryInfo['Serial Number'] ?? info?.serialNumber ?? 'unknown',
    factory_mac_address: factoryInfo['MAC Address'] ?? info?.macAddress ?? 'unknown',
    factory_model: factoryInfo.Model ?? info?.model ?? 'unknown',
    factory_hardware: factoryInfo.Hardware ?? info?.hardwareRevision ?? 'unknown',
    factory_firmware: factoryInfo.Firmware ?? info?.firmwareRevision ?? 'unknown',
    factory_manufacturer: factoryInfo.Manufacturer ?? info?.manufacturer ?? 'unknown',
    center_frequency_hz: info?.centerFrequency ?? 'unknown',
    current_frequency_hz: info?.currentFrequency ?? 'unknown',
    power_w: info?.powerWatts ?? 'unknown',
    battery_v: info?.voltageBattery ?? 'unknown',
    current_a: info?.current ?? 'unknown',
    temperature_pcb_c: info?.temperaturePCB ?? 'unknown',
    temperature_esp32_c: info?.temperatureESP32 ?? 'unknown',
    temperature_battery_c: info?.temperatureBattery ?? 'unknown',
    odometer_total_minutes: info?.odometerTotalMinutes ?? 'unknown',
  };
}

function buildDiagnosticFileName(metadata: DiagnosticMetadata): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');

  const rawSerial = String(metadata.factory_serial ?? metadata.device_id ?? 'knife');
  const safeSerial = rawSerial.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 48) || 'knife';

  return `seaus-diagnostic-${safeSerial}-${yyyy}${mm}${dd}-${hh}${min}${ss}.json`;
}
